/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2019-09-10 15:26:55
 *
 *******************************************************/
// @flow

import _ from 'lodash';

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Page, Layout, Stack, Icon } from '@shopify/polaris';
import { QuestionMarkMajor } from '@shopify/polaris-icons';
import queryString from 'query-string';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import moment from 'moment';

import MainMenu from 'src/components/containers/main-menu';
import DidYouKnow from 'src/components/views/subscription/did-you-know';
// import ExpiredModal from 'src/components/views/subscription/expired-modal';
import RegularExpiredModal from 'src/components/views/subscription/regular-expired-modal';
import SpecialDiscountCodeExpiredModal from 'src/components/views/subscription/special-discount-code-expired-modal';
import PasswordProtectedOfferExpiredModal from 'src/components/views/subscription/password-protected-offer-expired-modal';
import PlansReview from 'src/components/views/subscription/plans-review';
import PlansQA from 'src/components/views/subscription/plans-qa';
import ExceededPlanNotification from 'src/components/containers/common/exceeded-plan-notifications';
import ExceededPlanModal from 'src/components/views/subscription/exceeded-plan-modal';

import { URL } from 'src/constants/links';
import { EXTRA_FEATURES } from 'src/constants/plans-extra-feature';
import {
  getSubscribePlanInfo,
  allowToSubscribeBFCMCode,
  isSpecialDiscount2021Expired
} from 'src/helpers/subscription-helper';
import { getShop } from 'src/helpers';
import * as subscriptionActions from 'src/stores/actions/subscription-actions';

import history from 'src/routes/history';
import { routes } from 'src/routes';
import BFCM2022PartnerList from 'src/components/views/campaigns/bfcm2022/bfcm2022-partner-list';
import SpecialDiscountBlockNotification from 'src/components/views/campaigns/bfcm2022/special-discount-block-notification';

import SpecialDiscountExpiredModal from 'src/components/containers/common/notificationSubscription/special-discount-notification';

type Props = {
  host: string,
  selectedPlan: string,
  currentTotalProducts: number,
  subscribePlusPlanV2: boolean,
  allowToSubscribePlanV1: boolean,
  verifying: boolean,
  percent: number,
  confirmationUrl: string,
  subscribing: boolean,
  subscription: Object,
  verifyDiscountCode: Function,
  clearDiscountCode: Function,
  subscribe: Function
};

type ComposedProps = Props & WithI18nProps;

type State = {
  status: string,
  productsPlan: string,
  dayLeft: number,
  regularExpiredAt: String,
  correctPlan: Object,
  onSlidePrice: ?number,
  onSlideProducts: ?number,
  onSlidePlanName: ?string,
  showInformation: boolean,
  code: string,
  showVerifyCodeNotice: boolean,
  activeExpiredModal: boolean,
  activeRegularExpiredModal: boolean,
  activeExceededModal: boolean,
  dismissedBanner: boolean
};

function planWrapper(WrappedComponent: Object) {
  class HOCPlan extends Component<ComposedProps, State> {
    state = {
      status: 'trial',
      productsPlan: '1000',
      dayLeft: 0,
      regularExpiredAt: '',
      correctPlan: {},
      onSlidePrice: null,
      onSlideProducts: null,
      onSlidePlanName: null,
      showInformation: false,

      // Discount code
      code: '',
      showVerifyCodeNotice: false, // Show verify discount code result

      // Expired modal
      activeExpiredModal: false,

      // Expired modal for special discount code
      activeSpecialDiscountCodeExpiredModal: false,

      // Exceeded modal
      activeExceededModal: false,

      // Password protected offer expired modal
      activePasswordProtectedOfferExpiredModal: false,

      // Regular plan of shopify plus expired modal
      activeRegularExpiredModal: false,

      // Info banner
      dismissedBanner: false
    };

    componentDidMount() {
      const {
        allowToSubscribePlanV1,
        subscribePlusPlanV2,
        subscription,
        tenantApp = {}
      } = this.props;

      // Reset discount when push to discount page
      this.props.clearDiscountCode();

      // Not show popup special discount when push to plan page
      try {
        localStorage.setItem(
          `${tenantApp?.tenantId}-isNotShowSpecialDiscountAgain1Day`,
          moment().endOf('day').toISOString()
        );
      } catch (error) {}

      const specialDiscountExpiredAt = _.get(tenantApp, 'specialDiscountExpiredAt', 0);
      const passwordProtectedOfferExpired = _.get(
        tenantApp,
        'passwordProtectedOffer.isExpired',
        false
      );

      // Parse query params
      const queries = queryString.parse(this.props.location.search);
      const { showInfo: showInfoQuery = 'true' } = queries;

      // Get subscription status
      const {
        status,
        productsPlan,
        dayLeft,
        correctPlan = {},
        regularExpiredAt = ''
      } = subscription;

      // Check to show the information banner for pricing V2
      // Skip plan1, plan2_5 and shopify plus
      let showInformation = false;
      const { selectedPlan = '' } = this.props;
      const name = selectedPlan
        ? selectedPlan.replace('_discount', '').toLowerCase()
        : selectedPlan;
      const planNoChangeV2 = ['plan1', 'plan2_5'];
      if (
        name !== 'offer0_1' &&
        name.includes('plan') &&
        !allowToSubscribePlanV1 &&
        (!_.includes(planNoChangeV2, name) || subscribePlusPlanV2) &&
        status === 'subscribed' &&
        showInfoQuery === 'true' // force show or hide with query param
      ) {
        showInformation = true;
      }

      // Set initial plan for first loading
      const pathName = this.props.location.pathname;
      const selectedPlanData = getSubscribePlanInfo(
        selectedPlan,
        subscribePlusPlanV2,
        allowToSubscribePlanV1
      );

      const { onSlidePlanName, onSlidePrice, onSlideProducts } = this.initPriceSlider(
        pathName,
        selectedPlanData.name !== 'offer0_1' &&
          ['subscribed', 'specialDiscountExpiredSoon', 'exceededWithNotification'].includes(status)
          ? selectedPlanData
          : correctPlan
      );

      // Show special discount expired modal from day 8 after expired time
      const isBFCM2021Expired = isSpecialDiscount2021Expired(status, specialDiscountExpiredAt, 7);
      const dismissedBanner = this.getDismissedBanner();

      this.setState({
        status,
        productsPlan,
        dayLeft,
        regularExpiredAt,
        correctPlan,
        onSlidePlanName, // Minimal plan id
        onSlidePrice, // Minimal plan price
        onSlideProducts, // Minimal product count,
        showInformation, // Show information page for V2 pricing
        activeSpecialDiscountCodeExpiredModal: isBFCM2021Expired, // Show expired notice
        // activeExpiredModal:
        //   status === 'expired' && window.location.pathname !== '/plan-discount' ? true : false, // Show expired notice
        activeExceededModal: status === 'exceeded' ? true : false,
        activeRegularExpiredModal: status === 'regularExpired' ? true : false,
        activePasswordProtectedOfferExpiredModal: passwordProtectedOfferExpired,
        dismissedBanner: dismissedBanner === 'true' ? true : false // Show information banner
      });
    }

    componentWillReceiveProps(nextProps) {
      const { confirmationUrl } = nextProps;

      // Redirect to shopify confirmation page
      if (confirmationUrl) {
        window.top.location.href = confirmationUrl;
      }

      // Show / hide verify discount code result
      const { verifying: prevVerifying, subscribing: prevSubscribing } = this.props;
      const {
        verifying: nextVerifying,
        subscribing: nextSubscribing,
        subscribeSuccess
      } = nextProps;

      if (prevVerifying === true && nextVerifying === false) {
        // Show verification discount code
        this.setState({ showVerifyCodeNotice: true });
      } else {
        this.setState({ showVerifyCodeNotice: false });
      }

      // Subscribe failed
      if (prevSubscribing === true && nextSubscribing === false && subscribeSuccess === false) {
        history.push(routes.subscription.error());
      }
    }

    // Init the price slider
    initPriceSlider(pathName, correctPlan) {
      const { name, price, products } = correctPlan;

      // Should select large plan
      if ((pathName === '/plan' || pathName === '/plan-discount') && products > 50000) {
        return { onSlidePlanName: 'large', onSlidePrice: null, onSlideProducts: null };
      }

      return { onSlidePlanName: name, onSlidePrice: price, onSlideProducts: products };
    }

    render() {
      const {
        status,
        // activeExpiredModal,
        activeExceededModal,
        activeRegularExpiredModal,
        activeSpecialDiscountCodeExpiredModal,
        activePasswordProtectedOfferExpiredModal,
        dismissedBanner
      } = this.state;
      const { tenantApp, i18n } = this.props;
      const pathName = this.props.location.pathname;

      // Check applying BFCM Discount code
      const suggestBFCMCode = allowToSubscribeBFCMCode(tenantApp);

      const extraFeatures = this.getExtraFeatures();

      return (
        <div className="BC-Page BC-Page__Subscription">
          <MainMenu menuId="" />
          <Page
            title={i18n.translate('AdminHTML.Subscription.Subscription.title')}
            titleMetadata={
              <Stack spacing="tight">
                <span className="titleMetadata">
                  <a href={URL['SubscriptionDoc']} target="_blank" rel="noopener noreferrer">
                    <Icon source={QuestionMarkMajor} color="base" />
                  </a>
                </span>
              </Stack>
            }>
            <Layout>
              <Layout.Section>
                <div className="BC-Container__Notifications">
                  <SpecialDiscountBlockNotification />
                  {/* Information box */}
                  {!dismissedBanner ? (
                    <DidYouKnow
                      pathName={pathName}
                      onDismiss={this.handleDismissBanner}
                      suggestBFCMCode={suggestBFCMCode}
                    />
                  ) : (
                    ''
                  )}
                  {/* Sticky notification for special discount code */}
                  <SpecialDiscountExpiredModal />
                  {/* Sticky notification for exceeded plan */}
                  <ExceededPlanNotification />
                </div>
              </Layout.Section>

              <Layout.Section>
                {/* Wrapped component: Plan, Plan discount, Plan large*/}
                <WrappedComponent
                  {...this.props}
                  {...this.state}
                  suggestBFCMCode={suggestBFCMCode}
                  onSlide={this.onSlide}
                  handleChangeCode={this.handleChangeCode}
                  handleChangeShowInformation={this.handleChangeShowInformation}
                  extraFeatures={extraFeatures}
                />
              </Layout.Section>
              <Layout.Section>
                <BFCM2022PartnerList />
              </Layout.Section>
              <Layout.Section>
                <PlansReview />
              </Layout.Section>

              <Layout.Section>
                <PlansQA />
              </Layout.Section>
            </Layout>

            {/* Not show expired modal in BFCM campaign for page /plan */}
            {/* {!suggestBFCMCode && (
              <ExpiredModal active={activeExpiredModal} handleChange={this.handleChangeModal} />
            )} */}

            {/* Not show exceeded plan modal in BFCM campaign for page /plan */}
            {!suggestBFCMCode && (
              <ExceededPlanModal
                active={activeExceededModal}
                handleSubscribe={this.handleSubscribe}
              />
            )}

            {/* Show expired modal for special discount code */}
            {status === 'specialDiscountExpired' && (
              <SpecialDiscountCodeExpiredModal
                active={activeSpecialDiscountCodeExpiredModal}
                handleSubscribe={this.handleSubscribeAfterSpecialDiscount}
              />
            )}

            {/* Show expired modal for password protected offer */}
            <PasswordProtectedOfferExpiredModal
              active={activePasswordProtectedOfferExpiredModal}
              handleChange={this.handleChangePasswordProtectedModal}
            />

            {status === 'regularExpired' && (
              <RegularExpiredModal
                active={activeRegularExpiredModal}
                handleChange={this.handleChangeRegularExpiredModal}
                handleAction={this.handleChangeRegularExpiredModal}
              />
            )}
          </Page>
        </div>
      );
    }

    // Handle select plan
    onSlide = plan => {
      const { name, price, products } = plan;
      this.setState({ onSlidePlanName: name, onSlidePrice: price, onSlideProducts: products });
    };

    // Handle show/hide expired modal
    handleChangeModal = () => {
      this.setState(({ activeExpiredModal }) => ({
        activeExpiredModal: !activeExpiredModal
      }));
    };

    handleChangeRegularExpiredModal = () => {
      this.setState(({ activeRegularExpiredModal }) => ({
        activeRegularExpiredModal: !activeRegularExpiredModal
      }));
    };
    handleChangePasswordProtectedModal = () => {
      this.setState(({ activePasswordProtectedOfferExpiredModal }) => ({
        activePasswordProtectedOfferExpiredModal: !activePasswordProtectedOfferExpiredModal
      }));
    };

    // Handle subscribe exceeded modal
    handleSubscribe = () => {
      this.setState({ activeExceededModal: false });
      // history.push(routes.subscription.plan());
    };

    // Handle subscribe after active modal special discount bcfm 2021
    handleSubscribeAfterSpecialDiscount = () => {
      this.setState({ activeSpecialDiscountCodeExpiredModal: false });
    };
    // Get dismissed banner status
    getDismissedBanner = () => {
      const shop = getShop();
      let dismissed = false;
      try {
        const dismissedInStorage = localStorage.getItem(`${shop}-dismissBannerInSubscriptionPage`);
        if (dismissedInStorage === 'true') {
          dismissed = true;
        }
      } catch (error) {}

      return dismissed;
    };

    // Handle hide banner
    handleDismissBanner = () => {
      const shop = getShop();
      try {
        localStorage.setItem(`${shop}-dismissBannerInSubscriptionPage`, 'true');
      } catch (error) {}

      // Hide banner
      this.setState({ dismissedBanner: true });
    };

    // Change discount code
    handleChangeCode = code => {
      if (code) {
        this.setState({ code, showVerifyCodeNotice: false });
      } else {
        // Hide discount code verification result
        this.setState({ code, showVerifyCodeNotice: false });

        this.props.clearDiscountCode();
      }
    };

    // Handle change from information page to pricing page
    handleChangeShowInformation = () => {
      this.setState(showInformation => ({ showInformation: !showInformation }));
    };

    // Check extra features which the store has added
    getExtraFeatures = () => {
      const { tenantApp } = this.props;
      let extraFeatures = [];

      // Variants display
      const variantsDisplay = _.get(tenantApp, 'adminAppFeatures.variantsDisplay', false);
      if (variantsDisplay) {
        extraFeatures.push(EXTRA_FEATURES.VARIANTS_DISPLAY);
      }

      return extraFeatures;
    };
  }

  const mapDispatchToProps = (dispatch, ownProps) => ({
    verifyDiscountCode: code => dispatch(subscriptionActions.verifyDiscountCode(code)),
    clearDiscountCode: () => dispatch(subscriptionActions.clearDiscountCode()),
    subscribe: (planId, code, host) =>
      dispatch(subscriptionActions.subscribe(planId, code, host, ownProps.i18n))
  });

  const mapStateToProps = (state, props) => {
    const tenantApp = _.get(state.oauth, 'tenantApp', {});
    const host = _.get(state.oauth, 'host', '');
    const isShopifyPlus = _.get(tenantApp, 'externalInfo.plan_name', '') === 'shopify_plus';
    const allowToSubscribeRegularPlanV2 = _.get(tenantApp, 'allowToSubscribeRegularPlanV2', false);
    const allowToSubscribePlusPlanV2 = _.get(tenantApp, 'allowToSubscribePlusPlanV2', false);
    const subscribePlusPlanV2 =
      !allowToSubscribeRegularPlanV2 && (isShopifyPlus || allowToSubscribePlusPlanV2);
    const hasExtraFee = _.get(tenantApp, 'hasExtraFee', false);

    return {
      tenantApp,
      host,
      selectedPlan: _.get(state.oauth.tenantApp, 'selectedPlan'),
      currentTotalProducts: _.get(tenantApp, 'appParams.currentTotalProducts'),
      subscribePlusPlanV2,
      allowToSubscribePlanV1: _.get(tenantApp, 'allowToSubscribePlanV1'),
      verifying: _.get(state.subscription, 'verifying', false),
      percent: _.get(state.subscription, 'percent', 0),
      termsConditions: _.get(state.subscription, 'termsConditions', ''),
      confirmationUrl: _.get(state.subscription, 'confirmationUrl', ''),
      subscribing: _.get(state.subscription, 'subscribing', false),
      subscribeSuccess: _.get(state.subscription, 'subscribeSuccess', false),
      subscription: _.get(state.oauth, 'subscription', {}),
      hasExtraFee
    };
  };

  return withI18n()(connect(mapStateToProps, mapDispatchToProps)(HOCPlan));
}

export default planWrapper;
